import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import API_BASE from '../config';
import { Banner, Loading, Empty, Modal, SectionIntro, formatDateTime, errorMessage } from '../components/ui';
import './Inspectors.css';

const BLANK = { name: '', email: '' };

function Inspectors() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');

  const [editing, setEditing] = useState(null); // null | 'new' | row
  const [form, setForm] = useState(BLANK);

  let user = {};
  try { user = JSON.parse(localStorage.getItem('user') || '{}'); } catch { user = {}; }
  const isSuperAdmin = user.role === 'Super Admin';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/inspectors`, { params: { includeInactive: true } });
      setRows(res.data);
    } catch (err) {
      setError(errorMessage(err, 'Could not load inspectors.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(BLANK); setEditing('new'); };
  const openEdit = (row) => { setForm({ name: row.Name || '', email: row.Email }); setEditing(row); };

  const save = async () => {
    setBusy(true); setError('');
    try {
      if (editing === 'new') {
        await axios.post(`${API_BASE}/api/inspectors`, form);
        setSuccess(`${form.email} added.`);
      } else {
        await axios.put(`${API_BASE}/api/inspectors/${editing.Id}`, form);
        setSuccess(`${form.email} updated.`);
      }
      setEditing(null);
      await load();
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(false); }
  };

  const toggleActive = async (row) => {
    try {
      await axios.put(`${API_BASE}/api/inspectors/${row.Id}`, { isActive: !row.IsActive });
      setSuccess(`${row.Email} ${row.IsActive ? 'deactivated' : 'reactivated'}.`);
      await load();
    } catch (err) { setError(errorMessage(err)); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Remove ${row.Email}?`)) return;
    try {
      const res = await axios.delete(`${API_BASE}/api/inspectors/${row.Id}`);
      setSuccess(res.data.message);
      await load();
    } catch (err) { setError(errorMessage(err)); }
  };

  const filtered = rows.filter((r) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return [r.Name, r.Email].filter(Boolean).some((x) => String(x).toLowerCase().includes(q));
  });

  return (
    <div className="page-container">
      <div className="page-card">
        <div className="page-header">
          <div>
            <h2>Inspectors</h2>
            <p className="page-subtitle">
              The people who assess facilities. They cannot sign in — allocating an inspector to an
              application copies them on the outcome email, so they see exactly what the client was told.
            </p>
          </div>
          {isSuperAdmin && (
            <div className="page-header-actions">
              <button className="btn btn-primary" onClick={openNew}>Add inspector</button>
            </div>
          )}
        </div>

        <Banner kind="success" message={success} onDismiss={() => setSuccess('')} />
        <Banner kind="error" message={error} onDismiss={() => setError('')} />

        <div className="filter-row">
          <input
            className="filter-search"
            type="text"
            placeholder="Search name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search inspectors"
          />
          {search && (
            <button className="btn btn-ghost btn-sm filter-clear" onClick={() => setSearch('')}>
              Clear
            </button>
          )}
        </div>

        {loading ? <Loading /> : filtered.length === 0 ? (
          <Empty
            title={search ? 'No inspectors match that search.' : 'No inspectors yet.'}
            hint={search ? undefined : 'Add them here, then allocate one on each application.'}
          />
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th className="nowrap">Applications</th>
                  <th className="nowrap">Status</th>
                  <th className="nowrap">Added</th>
                  {isSuperAdmin && <th className="nowrap">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.Id} className={r.IsActive ? '' : 'insp-inactive'}>
                    <td><strong>{r.Name || '—'}</strong></td>
                    <td>
                      {r.Email}
                      {!/@afsq\.co\.za$/i.test(r.Email) && (
                        <span className="badge badge-amber insp-ext">External</span>
                      )}
                    </td>
                    <td className="numeric nowrap">{r.ApplicationCount || 0}</td>
                    <td className="nowrap">
                      <span className={`badge ${r.IsActive ? 'badge-green' : 'badge-grey'}`}>
                        {r.IsActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="nowrap insp-meta">
                      {formatDateTime(r.CreatedAt)}
                      {r.CreatedBy && <div>{r.CreatedBy}</div>}
                    </td>
                    {isSuperAdmin && (
                      <td className="nowrap">
                        <button className="btn btn-ghost btn-sm" onClick={() => openEdit(r)}>Edit</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => toggleActive(r)}>
                          {r.IsActive ? 'Deactivate' : 'Reactivate'}
                        </button>
                        <button className="btn btn-ghost btn-sm insp-remove" onClick={() => remove(r)}>Remove</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <SectionIntro>
          {filtered.length} of {rows.length} inspector{rows.length === 1 ? '' : 's'} shown.
          An inspector allocated to an application is CC'd on both approval and rejection emails.
        </SectionIntro>
      </div>

      {editing && (
        <Modal
          title={editing === 'new' ? 'Add an inspector' : `Edit ${editing.Email}`}
          subtitle="They receive a copy of client outcome emails. No sign-in account is created."
          onClose={() => setEditing(null)}
        >
          <div className="field">
            <label htmlFor="insp-name">Name</label>
            <input id="insp-name" type="text" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Thato Sekhotho" />
          </div>
          <div className="field">
            <label className="field-required" htmlFor="insp-email">Email address</label>
            <input id="insp-email" type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="thato.sekhotho@afsq.co.za" />
          </div>
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={busy || !form.email.trim()}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default Inspectors;
